import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useProfile } from './ProfileContext';
import AppNav from './AppNav';
import NotificationBell from './NotificationBell';
import './Card.css';
import './MessagesPage.css';
import './EmployerDashboard.css';

// MessagesPage is shared by veteran and employer sessions (an employer
// needs to see and reply to veterans who message them from a job posting).
// Employers don't have the veteran nav (Feed/Jobs/Network all redirect them
// straight back to /employer/dashboard) and shouldn't get the Compose/search
// feature, which is effectively unbuilt "Candidate Search" -- so this is a
// minimal nav variant instead of reusing AppNav for them.
function EmployerMessagesNav({ userId }) {
  const navigate = useNavigate();
  const logout = async () => { await supabase.auth.signOut(); navigate('/'); };
  return (
    <nav className="empd-nav">
      <Link className="empd-nav-logo" to="/employer/dashboard">
        Vet<span>Qualified</span> <span className="empd-nav-tag">for Employers</span>
      </Link>
      <div className="empd-nav-right">
        <NotificationBell userId={userId} />
        <button className="empd-logout" onClick={logout}>Sign Out</button>
      </div>
    </nav>
  );
}

function getAvatarColor() {
  return { color: 'var(--vq-secondary)', bg: 'var(--vq-raised)' };
}

function dayLabel(value) {
  return new Date(value).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function timeLabel(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
// ── Get all 1st-degree connection IDs (accepted only) ────────────
async function getFirstDegreeIds(userId) {
  const { data, error } = await supabase
    .from('connections')
    .select('requester_id, recipient_id')
    .eq('status', 'connected')
    .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`);
  if (error) throw error;

  return (data || []).map(c =>
    c.requester_id === userId ? c.recipient_id : c.requester_id
  );
}

// ── Get all pending connection IDs (sent by me) ──────────────────
async function getPendingIds(userId) {
  const { data, error } = await supabase
    .from('connections')
    .select('recipient_id')
    .eq('requester_id', userId)
    .eq('status', 'pending');
  if (error) throw error;

  return (data || []).map(c => c.recipient_id);
}

// ── Get 2nd-degree IDs (connections of my connections) ───────────
async function getSecondDegreeIds(userId, firstDegreeIds) {
  if (firstDegreeIds.length === 0) return new Set();

  const { data, error } = await supabase
    .from('connections')
    .select('requester_id, recipient_id')
    .eq('status', 'connected')
    .or(
      firstDegreeIds.map(id => `requester_id.eq.${id},recipient_id.eq.${id}`).join(',')
    );
  if (error) throw error;

  const secondIds = new Set();
  (data || []).forEach(c => {
    const other = firstDegreeIds.includes(c.requester_id) ? c.recipient_id : c.requester_id;
    if (other !== userId && !firstDegreeIds.includes(other)) {
      secondIds.add(other);
    }
  });
  return secondIds;
}

// ── Determine degree label ────────────────────────────────────────
function getDegree(memberId, firstIds, secondIds) {
  if (firstIds.includes(memberId))  return '1st';
  if (secondIds.has(memberId))      return '2nd';
  return '3rd+';
}
// ── Get or create a 1:1 conversation between two users ───────────
async function getOrCreateConversation(userIdA, userIdB) {
  if (!userIdA || !userIdB) return null;

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user?.id) {
    console.error('Unable to get authenticated user:', userError);
    return null;
  }

  const otherUserId = user.id === userIdA ? userIdB : userIdA;

  if (!otherUserId || otherUserId === user.id) {
    return null;
  }

  const { data: conversationId, error } = await supabase.rpc(
    'get_or_create_conversation',
    {
      other_user_id: otherUserId,
    }
  );

  if (error || !conversationId) {
    console.error('Unable to get or create conversation:', error);
    return null;
  }

  return conversationId;
}

export default function MessagesPage() {
  const location = useLocation();
  const { profile } = useProfile();
  const [activeTab,    setActiveTab]    = useState('messages');
  const [activeThread, setActiveThread] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [sendErrors, setSendErrors] = useState({});
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [messageError, setMessageError] = useState('');
  const [inboxError, setInboxError] = useState('');
  const [requestError, setRequestError] = useState('');
  const [composeError, setComposeError] = useState('');
  const [composeScope, setComposeScope] = useState('contacts');
  const [showLatest, setShowLatest] = useState(false);
  const [threads,      setThreads]      = useState([]);
  const [requests,     setRequests]     = useState([]);
  const [messages,     setMessages]     = useState([]);
  const [searchVal,    setSearchVal]    = useState('');
  const [sending,      setSending]      = useState(false);
  const [userId,       setUserId]       = useState(null);
  const [accountType,  setAccountType]  = useState(null); // 'veteran' | 'employer'
  const [loadingMsgs,  setLoadingMsgs]  = useState(false);
  const [composeOpen,     setComposeOpen]     = useState(false);
  const [composeSearch,   setComposeSearch]   = useState('');
  const [composeResults,  setComposeResults]  = useState([]);
  const [composeLoading,  setComposeLoading]  = useState(false);
  const [firstDegreeIds,  setFirstDegreeIds]  = useState([]);
  const [secondDegreeIds, setSecondDegreeIds] = useState(new Set());
  const [pendingIds,      setPendingIds]      = useState([]);
  const [sendingConnect,  setSendingConnect]  = useState({});
  const messagesEndRef      = useRef(null);
  const inputRef            = useRef(null);
  const realtimeRef         = useRef(null);
  const sentMessageIds      = useRef(new Set());
  const threadListChannelRef = useRef(null);
  const activeThreadRef      = useRef(null);
  const messageLoadRef = useRef(0);
  const sendLockRef = useRef(false);
  const nearBottomRef = useRef(true);
  const scrollRef = useRef(null);
  const composeModalRef = useRef(null);
  const composeVersionRef = useRef(0);
  const inputVal = drafts[activeThread?.id] || '';
  const setInputVal = value => setDrafts(prev => ({ ...prev, [activeThread.id]: value }));


  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() || '?';

  // ── Load conversations ────────────────────────────────────────
  const loadThreads = useCallback(async (uid) => {
    // Get all conversation IDs + read timestamps for this user
    const { data: myParts, error: partsError } = await supabase
      .from('conversation_participants')
      .select('conversation_id, last_read_at')
      .eq('user_id', uid);

    if (partsError) { setInboxError('Conversations could not load. Please try again.'); return; }
    setInboxError('');
    if (!myParts || myParts.length === 0) { setThreads([]); return; }

    const convIds = myParts.map(p => p.conversation_id);
    const readMap = {};
    myParts.forEach(p => { readMap[p.conversation_id] = p.last_read_at; });

    // Get conversation metadata
    const { data: convData } = await supabase
      .from('conversations')
      .select('id, last_message_at, created_by')
      .in('id', convIds)
      .order('last_message_at', { ascending: false });

    if (!convData || convData.length === 0) { setThreads([]); return; }

    // Get last message per conversation
    const lastMsgPromises = convIds.map(cid =>
      supabase
        .from('messages')
        .select('content, created_at, sender_id')
        .eq('conversation_id', cid)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
    );
    const lastMsgResults = await Promise.all(lastMsgPromises);
    const lastMsgMap = {};
    lastMsgResults.forEach((res, i) => {
      if (res.data) lastMsgMap[convIds[i]] = res.data;
    });

    // Resolve the other participant via get_conversation_participants --
    // conversation_participants itself is only readable for the caller's
    // own row, so this can't be inferred with a plain client-side query.
    // (Guessing from message sender_ids used to 406 whenever the current
    // user sent the only message and hadn't been replied to yet, silently
    // dropping that conversation from their own thread list.)
    const participantResults = await Promise.all(
      convIds.map(cid => supabase.rpc('get_conversation_participants', { p_conversation_id: cid }))
    );
    const otherIdMap = {};
    participantResults.forEach((res, i) => {
      const other = (res.data || []).find(p => p.user_id !== uid);
      if (other) otherIdMap[convIds[i]] = other.user_id;
    });

    const otherIds = [...new Set(Object.values(otherIdMap))].filter(Boolean);
    if (otherIds.length === 0) { setThreads([]); return; }

    // Conversations can have a member or employer on the other side.
    // Keep member service details subject to their visibility settings.
    const [{ data: memberProfiles }, { data: employerProfiles }] = await Promise.all([
      supabase.from('profiles_public').select('id, first_name, last_name, avatar_url, rank, branch').in('id', otherIds),
      supabase.from('employer_profiles_public').select('id, company_name, logo_url').in('id', otherIds),
    ]);

    const profileMap = {};
    (memberProfiles || []).forEach(p => { profileMap[p.id] = p; });
    (employerProfiles || []).forEach(p => {
      profileMap[p.id] = {
        id: p.id,
        first_name: p.company_name || 'Employer',
        last_name: '',
        avatar_url: p.logo_url,
        branch: '',
        rank: '',
        kind: 'employer',
      };
    });

    // Build thread objects
    const built = (convData || []).map(conv => {
      const otherId = otherIdMap[conv.id];
      if (!otherId) return null;
      const other = profileMap[otherId];
      if (!other) return null;
      const { color, bg } = getAvatarColor(other.id);
      const lastMsg = lastMsgMap[conv.id];
      const lastReadAt = readMap[conv.id];
      const unread = lastMsg &&
        lastMsg.sender_id !== uid &&
        new Date(lastMsg.created_at) > new Date(lastReadAt) ? 1 : 0;

      return {
        id:          conv.id,
        otherId:     other.id,
        name:        `${other.first_name || ''} ${other.last_name || ''}`.trim(),
        initials:    `${other.first_name?.[0] || ''}${other.last_name?.[0] || ''}`.toUpperCase(),
        role:        other.kind === 'employer' ? 'Employer' : [other.branch, other.rank].filter(Boolean).join(' · '),
        color,
        bg,
        avatarUrl:   other.avatar_url || '',
        unread,
        lastMessage: lastMsg?.content || '',
        time:        timeLabel(conv.last_message_at),
      };
    }).filter(Boolean);

    setThreads(built);
  }, []);

  // ── Load pending message requests ────────────────────────────
  const loadRequests = useCallback(async (uid) => {
    const { data } = await supabase
      .from('message_requests')
      .select('id, from_user_id, intro_message, created_at')
      .eq('to_user_id', uid)
      .eq('status', 'pending');

    if (!data || data.length === 0) { setRequests([]); return; }

    const fromIds = data.map(r => r.from_user_id);
    const { data: profilesData } = await supabase
      .from('profiles_public')
      .select('id, first_name, last_name, avatar_url, branch, rank')
      .in('id', fromIds);

    const profileMap = {};
    (profilesData || []).forEach(p => { profileMap[p.id] = p; });

    const built = data.map(req => {
      const sender = profileMap[req.from_user_id];
      if (!sender) return null;
      const { color, bg } = getAvatarColor(sender.id);
      return {
        id:       req.id,
        fromId:   req.from_user_id,
        name:     `${sender.first_name || ''} ${sender.last_name || ''}`.trim(),
        initials: `${sender.first_name?.[0] || ''}${sender.last_name?.[0] || ''}`.toUpperCase(),
        role:     [sender.branch, sender.rank].filter(Boolean).join(' · '),
        color,
        bg,
        intro:    req.intro_message || '',
        time:     timeLabel(req.created_at),
      };
    }).filter(Boolean);

    setRequests(built);
  }, []);

  const loadMessages = useCallback(async (convId, uid) => {
    const version = ++messageLoadRef.current;
    setLoadingMsgs(true);
    setMessageError('');
    const { data, error } = await supabase.from('messages')
      .select('id, sender_id, content, created_at')
      .eq('conversation_id', convId).order('created_at', { ascending: true });
    if (version !== messageLoadRef.current || activeThreadRef.current?.id !== convId) return;
    setLoadingMsgs(false);
    if (error) { setMessageError('Messages could not load. Please try again.'); return; }
    // Keep any realtime message that arrived while history was loading.
    setMessages(prev => [...(data || []), ...prev.filter(m => !(data || []).some(row => row.id === m.id))]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    await supabase.from('conversation_participants')
      .update({ last_read_at: new Date().toISOString() }).eq('conversation_id', convId).eq('user_id', uid);
    setThreads(prev => prev.map(t => t.id === convId ? { ...t, unread: 0 } : t));
  }, []);

  const handleSelectThread = useCallback(thread => {
    activeThreadRef.current = thread;
    setActiveThread(thread);
    setActiveTab('messages');
    setMessages([]);
    setShowLatest(false);
    nearBottomRef.current = true;
    if (userId) loadMessages(thread.id, userId);
  }, [userId, loadMessages]);

  // ── Mount: get user, load data ────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) return;
      const uid = session.user.id;
      setUserId(uid);
      supabase.rpc('get_account_type').then(({ data }) => setAccountType(data || null));
      await loadThreads(uid);
      await loadRequests(uid);
      setLoadingThreads(false);

      // A brand-new, message-less conversation (e.g. from JobsPage's
      // "Message Employer") can't be resolved by loadThreads' inference --
      // it identifies the other participant from message sender_ids or
      // conversations.created_by, neither of which distinguishes "other
      // person" when the current user is both the creator and the only
      // sender so far. Same workaround handleComposeSelect already uses:
      // open the thread directly from the identity the caller already knows,
      // rather than relying on the list to resolve it.
      const opened = location.state?.openConversation;
      if (opened?.id) {
        const openedThread = {
          id: opened.id,
          otherId: opened.otherId,
          name: opened.name,
          initials: opened.initials,
          role: opened.role || '',
          color: '#C9A84C',
          bg: 'rgba(201,168,76,0.15)',
          avatarUrl: '',
          unread: 0,
          lastMessage: '',
          time: '',
          };
        activeThreadRef.current = openedThread;
        setActiveThread(openedThread);
        await loadMessages(opened.id, uid);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadThreads, loadRequests]);

  // ── Realtime: refresh thread list when added to a new conversation ──
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`participant-watch:${userId}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'conversation_participants',
        filter: `user_id=eq.${userId}`,
      }, () => {
        loadThreads(userId);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, loadThreads]);

  // ── Keep activeThreadRef in sync so the thread-list subscription ──
  // ── doesn't need to re-subscribe every time the active thread changes ──
  useEffect(() => {
    activeThreadRef.current = activeThread;
  }, [activeThread]);

  // ── Realtime: refresh thread list when a message arrives in any ──
  // ── background conversation (not the currently open thread) ─────
  useEffect(() => {
    if (!userId) return;

    if (threadListChannelRef.current) {
      supabase.removeChannel(threadListChannelRef.current);
    }

    const channel = supabase
      .channel(`thread-list-msgs:${userId}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'messages',
      }, (payload) => {
        const msg = payload.new;
        // Skip messages we sent — send flow already updates state
        if (msg.sender_id === userId) return;
        // Skip messages in the active thread — per-thread subscription handles those
        if (activeThreadRef.current && msg.conversation_id === activeThreadRef.current.id) return;
        // A message arrived in a background conversation — refresh the thread list
        loadThreads(userId);
      })
      .subscribe();

    threadListChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      threadListChannelRef.current = null;
    };
  }, [userId, loadThreads]);

  // One subscription follows the selected conversation, including navigation/contacts.
  useEffect(() => {
    if (!activeThread?.id || !userId) return undefined;
    const convId = activeThread.id;
    const channel = supabase.channel(`messages:${convId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${convId}` }, ({ new: msg }) => {
        if (activeThreadRef.current?.id !== convId || sentMessageIds.current.has(msg.id)) return;
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        setThreads(prev => prev.map(t => t.id === convId ? { ...t, lastMessage: msg.content, time: 'just now' } : t));
      }).subscribe();
    realtimeRef.current = channel;
    return () => { supabase.removeChannel(channel); realtimeRef.current = null; };
  }, [activeThread?.id, userId]);

  // ── Cleanup realtime on unmount ───────────────────────────────
  useEffect(() => {
    return () => {
      messageLoadRef.current += 1;
      composeVersionRef.current += 1;
      if (realtimeRef.current) supabase.removeChannel(realtimeRef.current);
      if (threadListChannelRef.current) supabase.removeChannel(threadListChannelRef.current);
    };
  }, []);

  // Do not pull someone away from the earlier messages they are reading.
  useEffect(() => {
    if (nearBottomRef.current) {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    } else setShowLatest(true);
  }, [messages]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = 'auto';
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 144)}px`;
    if (nearBottomRef.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [inputVal, activeThread?.id]);

  const handleSend = async () => {
    const text = inputVal.trim();
    if (!text || sendLockRef.current || !activeThread || !userId) return;
    const thread = activeThread;
    sendLockRef.current = true;
    setSending(true);
    setSendErrors(prev => ({ ...prev, [thread.id]: '' }));
    try {
      const { data: newMsg, error } = await supabase.from('messages').insert({
        conversation_id: thread.id, sender_id: userId, content: text,
      }).select('id, sender_id, content, created_at').single();
      if (error || !newMsg) throw new Error('Message was not saved');
      sentMessageIds.current.add(newMsg.id);
      setDrafts(prev => ({ ...prev, [thread.id]: '' }));
      if (activeThreadRef.current?.id === thread.id) {
        nearBottomRef.current = true;
        setShowLatest(false);
        setMessages(prev => prev.some(m => m.id === newMsg.id) ? prev : [...prev, newMsg]);
      }
      setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, lastMessage: text, time: 'just now' } : t));
      // Notification failure must not turn a saved message into a retry/duplicate.
      await supabase.from('notifications').insert({
        user_id: thread.otherId, actor_id: userId, type: 'new_message', entity_id: thread.id,
      }).then(() => {}, () => {});
    } catch {
      setSendErrors(prev => ({ ...prev, [thread.id]: 'Message not sent. Your draft is still here. Try Send again.' }));
    } finally {
      sendLockRef.current = false;
      setSending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      e.preventDefault(); handleSend();
    }
  };

  // ── Accept message request ────────────────────────────────────
  const handleAccept = async (req) => {
    if (!userId) return;

    // Atomically accept request, ensure conversation, and insert intro
    // as the original sender via SECURITY DEFINER RPC. This avoids the
    // RLS denial that would otherwise occur if the client tried to insert
    // a messages row with sender_id = req.fromId (which is not auth.uid()).
    const { data: convId, error: acceptError } = await supabase.rpc(
      'accept_message_request',
      { request_id: req.id }
    );

    if (acceptError || !convId) {
      setRequestError('Request could not be accepted. Please try again.');
      return;
    }

    setRequests(prev => prev.filter(r => r.id !== req.id));
    await loadThreads(userId);

    setRequestError('');
    handleSelectThread({ id: convId, otherId: req.fromId, name: req.name, initials: req.initials,
      role: req.role, color: req.color, bg: req.bg, avatarUrl: '', unread: 0, lastMessage: req.intro, time: req.time });
  };

  const handleDecline = async reqId => {
    const { error } = await supabase.from('message_requests').update({ status: 'declined' }).eq('id', reqId);
    if (error) { setRequestError('Request could not be declined. Please try again.'); return; }
    setRequestError('');
    setRequests(prev => prev.filter(r => r.id !== reqId));
  };

  const openCompose = async () => {
    setComposeOpen(true);
    setComposeSearch('');
    setComposeScope('contacts');
    setComposeResults([]);
    setComposeError('');
    if (!userId) return;
    const version = ++composeVersionRef.current;
    setComposeLoading(true);
    try {
      const first = await getFirstDegreeIds(userId);
      const second = await getSecondDegreeIds(userId, first);
      const pending = await getPendingIds(userId);
      const { data, error } = await supabase.from('profiles_public')
        .select('id, first_name, last_name, avatar_url, branch, rank, occupation_title').neq('id', userId);
      if (version !== composeVersionRef.current) return;
      if (error) throw error;
      setFirstDegreeIds(first);
      setSecondDegreeIds(second);
      setPendingIds(pending);
      setComposeResults(data || []);
    } catch {
      if (version === composeVersionRef.current) setComposeError('Contacts could not load. Close this window and try again.');
    } finally {
      if (version === composeVersionRef.current) setComposeLoading(false);
    }
  };

  useEffect(() => {
    if (!composeOpen) return undefined;
    const trigger = document.activeElement;
    const modal = composeModalRef.current;
    modal?.querySelector('input')?.focus();
    const handleDialogKey = event => {
      if (event.key === 'Escape') { setComposeOpen(false); return; }
      if (event.key !== 'Tab') return;
      const items = Array.from(modal.querySelectorAll('button:not(:disabled), input, a[href]'));
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', handleDialogKey);
    return () => { document.removeEventListener('keydown', handleDialogKey); trigger?.focus(); };
  }, [composeOpen]);

  const handleComposeSelect = async member => {
    if (!userId) return;
    setComposeError('');
    const convId = await getOrCreateConversation(userId, member.id);
    if (!convId) { setComposeError('Conversation could not open. Please try again.'); return; }
    setComposeOpen(false);
    handleSelectThread({
      id: convId, otherId: member.id,
      name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
      initials: `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase(),
      role: [member.branch, member.rank].filter(Boolean).join(' · '),
      ...getAvatarColor(), avatarUrl: member.avatar_url || '', unread: 0, lastMessage: '', time: '',
    });
    await loadThreads(userId);
  };

  const handleComposeConnect = async (memberId) => {
    if (!userId || sendingConnect[memberId]) return;
    setSendingConnect(prev => ({ ...prev, [memberId]: true }));
    const { error } = await supabase.from('connections').insert({
      requester_id: userId,
      recipient_id: memberId,
      status: 'pending',
    });
    if (error) {
      setComposeError('Connection request was not sent. Please try again.');
      setSendingConnect(prev => ({ ...prev, [memberId]: false }));
      return;
    }
    setPendingIds(prev => [...prev, memberId]);
    setSendingConnect(prev => ({ ...prev, [memberId]: false }));

    await supabase.from('notifications').insert({
      user_id:   memberId,
      actor_id:  userId,
      type:      'connection_request',
      entity_id: memberId,
    });
  };
  const filtered = threads.filter(t =>
    !searchVal || t.name.toLowerCase().includes(searchVal.toLowerCase())
  );

  const visibleContacts = composeResults.filter(member =>
    (composeScope === 'contacts' ? firstDegreeIds.includes(member.id) : composeSearch.trim().length >= 2) &&
    `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase().includes(composeSearch.trim().toLowerCase())
  ).sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));

  const totalUnread = threads.reduce((sum, t) => sum + t.unread, 0);

  return (
    <div className="msg-root vq-surface">
      {accountType === 'employer' ? <EmployerMessagesNav userId={userId} /> : <AppNav />}
      <div className={`msg-shell${activeThread ? ' thread-open' : ''}`}>

        {/* ── LEFT PANEL ── */}
        <aside className="msg-left">
          <div className="msg-left-header">
            <div className="msg-left-title">
              <div><span className="msg-eyebrow">Your veteran network</span><h1>Messages</h1></div>
              {totalUnread > 0 && <span className="msg-unread-badge">{totalUnread}</span>}
            </div>
            {accountType === 'veteran' && <button className="msg-compose-btn" onClick={openCompose}>
              <span>New message</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </button>}
          </div>

          <div className="msg-search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search conversations"
              aria-label="Search conversations"
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              className="msg-search"
            />
          </div>

          <div className="msg-tab-bar">
            <button className={`msg-tab ${activeTab === 'messages' ? 'active' : ''}`} aria-pressed={activeTab === 'messages'} onClick={() => setActiveTab('messages')}>
              Messages
              {totalUnread > 0 && <span className="msg-tab-dot" />}
            </button>
            <button className={`msg-tab ${activeTab === 'requests' ? 'active' : ''}`} aria-pressed={activeTab === 'requests'} onClick={() => setActiveTab('requests')}>
              Requests
              {requests.length > 0 && <span className="msg-tab-count">{requests.length}</span>}
            </button>
          </div>

          {activeTab === 'messages' && (
            <div className="msg-thread-list">
              {loadingThreads && <div className="msg-empty-list" role="status">Loading your conversations…</div>}
              {inboxError && <div className="msg-error" role="alert">{inboxError}<button onClick={() => loadThreads(userId)}>Try again</button></div>}
              {!loadingThreads && !inboxError && filtered.length === 0 && (
                <div className="msg-empty-list">
                  <strong>{threads.length === 0 ? 'Your next connection starts here.' : 'No matching conversations'}</strong>
                  <p>{threads.length === 0 ? 'Reach out to a connection about a career move, shared experience, or your next step.' : 'Try another name to find someone in your inbox.'}</p>
                  {accountType === 'veteran' && threads.length === 0 && <button className="msg-primary" onClick={openCompose}>Find a contact</button>}
                </div>
              )}
              {filtered.map(thread => (
                <button
                  key={thread.id}
                  className={`msg-thread-item ${activeThread?.id === thread.id ? 'active' : ''} ${thread.unread > 0 ? 'unread' : ''}`}
                  aria-current={activeThread?.id === thread.id ? 'true' : undefined}
                  onClick={() => handleSelectThread(thread)}
                >
                  <div className="msg-thread-av-wrap">
                    {thread.avatarUrl
                      ? <img src={thread.avatarUrl} alt="" className="msg-thread-av-img" />
                      : <div className="msg-thread-av" style={{ background: thread.bg, color: thread.color }}>{thread.initials}</div>
                    }
                  </div>
                  <div className="msg-thread-info">
                    <div className="msg-thread-top">
                      <span className="msg-thread-name">{thread.name}</span>
                      <span className="msg-thread-time">{thread.time}</span>
                    </div>
                    <div className="msg-thread-role">{thread.role}</div>
                    <div className="msg-thread-preview">{drafts[thread.id] ? <><strong>Draft: </strong>{drafts[thread.id]}</> : thread.lastMessage || 'Start the conversation'}</div>
                  </div>
                  {thread.unread > 0 && <span className="msg-thread-unread">{thread.unread}</span>}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="msg-request-list">
              {requestError && <div className="msg-error" role="alert">{requestError}</div>}
              {requests.length === 0 && <div className="msg-empty-list"><strong>No pending requests</strong><p>New message introductions will appear here for you to review.</p></div>}
              {requests.map(req => (
                <div key={req.id} className="msg-request-item">
                  <div className="msg-request-top">
                    <div className="msg-thread-av" style={{ background: req.bg, color: req.color, width: 40, height: 40, fontSize: '0.82rem' }}>
                      {req.initials}
                    </div>
                    <div className="msg-request-info">
                      <div className="msg-request-name">{req.name}</div>
                      <div className="msg-request-role">{req.role}</div>
                      <div className="msg-request-time">{req.time}</div>
                    </div>
                  </div>
                  <p className="msg-request-intro">"{req.intro}"</p>
                  <div className="msg-request-actions">
                    <button className="msg-req-accept" onClick={() => handleAccept(req)}>Accept</button>
                    <button className="msg-req-decline" onClick={() => handleDecline(req.id)}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* ── RIGHT PANEL ── */}
        <main className="msg-right">
          {activeThread ? (
            <>
              <div className="msg-conv-header">
                <button
                  className="msg-back-btn"
                  type="button"
                  aria-label="Back to messages"
                  onClick={() => { activeThreadRef.current = null; messageLoadRef.current += 1; setActiveThread(null); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div className="msg-conv-av-wrap">
                  {activeThread.avatarUrl
                    ? <img src={activeThread.avatarUrl} alt="" className="msg-conv-av-img" />
                    : <div className="msg-conv-av" style={{ background: activeThread.bg, color: activeThread.color }}>{activeThread.initials}</div>
                  }
                </div>
                <div className="msg-conv-info">
                  <div className="msg-conv-name">
                    {activeThread.name}
                  </div>
                  <div className="msg-conv-role">
                    {activeThread.role || 'Direct conversation'}
                  </div>
                </div>
              </div>

              <div className="msg-messages" ref={scrollRef} role="log" aria-label="Conversation messages" aria-live="polite" aria-busy={loadingMsgs}
                onScroll={e => { const el = e.currentTarget; nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; if (nearBottomRef.current) setShowLatest(false); }}>
                <div className="msg-encrypted-notice">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  Share thoughtfully. Keep personal records and sensitive service details out of messages.
                </div>

                {loadingMsgs && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted, #8FA3BE)', fontSize: '0.82rem' }}>
                    Loading messages…
                  </div>
                )}

                {messageError && <div className="msg-error" role="alert">{messageError}<button onClick={() => loadMessages(activeThread.id, userId)}>Try again</button></div>}
                {!loadingMsgs && !messageError && messages.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted, #8FA3BE)', fontSize: '0.82rem' }}>
                    No messages yet. Say hello!
                  </div>
                )}

                {messages.map((msg, index) => {
                  const isMe = msg.sender_id === userId;
                  return (
                    <Fragment key={msg.id}>
                    {(index === 0 || dayLabel(messages[index - 1].created_at) !== dayLabel(msg.created_at)) && <div className="msg-date-divider"><span>{dayLabel(msg.created_at)}</span></div>}
                    <div className={`msg-bubble-row ${isMe ? 'me' : 'them'}`}>
                      {!isMe && (
                        <div className="msg-bubble-av" style={{ background: activeThread.bg, color: activeThread.color }}>
                          {activeThread.initials}
                        </div>
                      )}
                      <div className="msg-bubble-group">
                        <div className={`msg-bubble ${isMe ? 'me' : 'them'}`}>
                          {msg.content}
                        </div>
                        <div className={`msg-bubble-time ${isMe ? 'me' : ''}`}>
                          <time dateTime={msg.created_at} title={new Date(msg.created_at).toLocaleString()}>{new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}{isMe ? ' · Sent' : ''}</time>
                        </div>
                      </div>
                      {isMe && (
                        <div className="msg-bubble-av me" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}>
                          {initials}
                        </div>
                      )}
                    </div>
                    </Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {showLatest && <button className="msg-latest" onClick={() => { nearBottomRef.current = true; setShowLatest(false); if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }}>Go to latest message ↓</button>}
              <div className="msg-input-area">
                {sendErrors[activeThread.id] && <p className="msg-error" role="alert">{sendErrors[activeThread.id]}</p>}
                <div className="msg-input-wrap">
                  <textarea
                    ref={inputRef}
                    className="msg-input"
                    placeholder="Write a message…"
                    aria-label={`Message ${activeThread.name}`}
                    disabled={sending}
                    value={inputVal}
                    onChange={e => {
                      setInputVal(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onKeyDown={handleKeyDown}
                    rows={1}
                  />
                  <div className="msg-input-actions">
                    <button
                      aria-label={sending ? 'Sending message' : 'Send message'}
                      className={`msg-send-btn ${inputVal.trim() ? 'active' : ''}`}
                      onClick={handleSend}
                      disabled={!inputVal.trim() || sending}
                    >
                      <span>{sending ? 'Sending…' : 'Send'}</span>
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="msg-input-hint">Enter to send · Shift+Enter for a new line <span>Drafts stay here while this page is open.</span></div>
              </div>
            </>
          ) : (
            <div className="msg-empty-state">
              <div className="msg-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
              </div>
              <span className="msg-eyebrow">One conversation can open a door</span>
              <h2>Stay in touch. Move forward.</h2>
              <p>Talk through a career move, exchange advice, and keep up with the people in your veteran network.</p>
              {accountType === 'veteran' && <button className="msg-primary" onClick={openCompose}>Start a conversation</button>}
              <span className="msg-empty-note">Choose a conversation or review your message requests.</span>
            </div>
          )}
</main>
      </div>

      {/* ── COMPOSE MODAL ── */}
      {composeOpen && (
        <div
          className="msg-compose-overlay"
          onClick={e => { if (e.target === e.currentTarget) setComposeOpen(false); }}
        >
          <div className="msg-compose-modal" ref={composeModalRef} role="dialog" aria-modal="true" aria-labelledby="msg-compose-title">
            <div className="msg-compose-header">
              <div><span className="msg-eyebrow">People in your corner</span><h2 id="msg-compose-title">New message</h2></div>
              <button aria-label="Close new message" className="msg-compose-close" onClick={() => setComposeOpen(false)}>×</button>
            </div>
            <p className="msg-compose-intro">Message a connection, or find a member and connect first.</p>
            <div className="msg-tab-bar">
              <button className={`msg-tab ${composeScope === 'contacts' ? 'active' : ''}`} aria-pressed={composeScope === 'contacts'} onClick={() => setComposeScope('contacts')}>My contacts</button>
              <button className={`msg-tab ${composeScope === 'members' ? 'active' : ''}`} aria-pressed={composeScope === 'members'} onClick={() => setComposeScope('members')}>Find members</button>
            </div>
            <div className="msg-compose-search-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search by name…"
                aria-label="Search people"
                value={composeSearch}
                onChange={e => setComposeSearch(e.target.value)}
                className="msg-compose-input"
              />
            </div>
            <div className="msg-compose-results">
              {composeError && <div className="msg-error" role="alert">{composeError}</div>}
              {composeLoading && <div className="msg-compose-empty" role="status">Loading your contacts…</div>}
              {!composeLoading && !composeError && visibleContacts.length === 0 && <div className="msg-compose-empty">
                <strong>{composeScope === 'contacts' ? 'No matching contacts yet' : composeSearch.trim().length < 2 ? 'Find your people' : 'No members found'}</strong>
                <p>{composeScope === 'contacts' ? 'Accepted connections appear here. Use Find members to search for someone you know.' : 'Enter at least two letters of a name. You can message after connecting.'}</p>
              </div>}
              {!composeLoading && visibleContacts.map(member => {
                const { color, bg } = getAvatarColor(member.id);
                const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
                const degree    = getDegree(member.id, firstDegreeIds, secondDegreeIds);
                const isPending = pendingIds.includes(member.id);
                const isFirst   = degree === '1st';
                return (
                  <div key={member.id} className="msg-compose-result-row">
                    {member.avatar_url
                      ? <img src={member.avatar_url} alt="" className="msg-compose-av-img" />
                      : <div className="msg-compose-av" style={{ background: bg, color }}>{initials}</div>
                    }
                    <div className="msg-compose-result-info">
                      <div className="msg-compose-result-name">
                        {member.first_name} {member.last_name}
                        <span className={`msg-degree-badge degree-${degree.replace('+','plus')}`}>
                          {isFirst ? 'Connected' : degree === '2nd' ? '2nd degree' : 'Member'}
                        </span>
                      </div>
                      <div className="msg-compose-result-role">
                        {[member.branch, member.occupation_title || member.rank].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="msg-compose-result-action">
                      {isFirst ? (
                        <button
                          className="msg-compose-action-btn primary"
                          onClick={() => handleComposeSelect(member)}
                        >
                          Message
                        </button>
                      ) : isPending ? (
                        <button className="msg-compose-action-btn pending" disabled>
                          Pending
                        </button>
                      ) : (
                        <button
                          className="msg-compose-action-btn connect"
                          onClick={() => handleComposeConnect(member.id)}
                          disabled={!!sendingConnect[member.id]}
                        >
                          {sendingConnect[member.id] ? '…' : 'Connect'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
